import { useState } from 'react'
import {
  Image as ImageIcon,
  Filter,
  FolderPlus,
  Folders,
  Trash2,
  ChevronRight,
  Pencil,
  LayoutGrid,
  Plus,
} from 'lucide-react'
import {
  Panel,
  Group as PanelGroup,
  Separator as PanelResizeHandle,
} from 'react-resizable-panels'
import { useVisualTasks, useVisualTask, useTaskImages, useTaskThumbnails } from '../hooks/useVisualTasks'
import { useVisualCollections, useVisualCollectionDetail } from '../hooks/useVisualCollections'
import { VisualTaskCard } from '../components/visual/VisualTaskCard'
import { VisualTaskDetail } from '../components/visual/VisualTaskDetail'
import { VisualTaskForm } from '../components/visual/VisualTaskForm'
import { CanvasWorkspace } from '../components/visual/CanvasWorkspace'
import { EmptyState } from '../components/ui/EmptyState'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { QueryErrorState } from '../components/ui/QueryErrorState'
import type { VisualTask } from '../lib/api'

// ─── Mobile Tab Types ─────────────────────────────────────────────────────────
type MobileTab = 'crear' | 'galeria' | 'sesion'

// ─── Resize handle ────────────────────────────────────────────────────────────
function ResizeHandle({ className = '' }: { className?: string }) {
  return (
    <PanelResizeHandle
      className={`
        group relative flex items-center justify-center
        w-2 bg-slate-100 hover:bg-emerald-100 active:bg-emerald-200
        transition-colors cursor-col-resize
        ${className}
      `}
    >
      <div className="h-8 w-0.5 rounded-full bg-slate-300 group-hover:bg-emerald-400 transition-colors" />
    </PanelResizeHandle>
  )
}

// ─── Collections Sidebar content (shared by desktop + mobile) ─────────────────
interface CollectionsSidebarProps {
  collections: { id: string; name: string }[]
  isLoadingCollections: boolean
  selectedCollectionId: string | null
  onSelectCollection: (id: string | null) => void
  onDeleteCollection: (id: string) => void
  showNewCollectionInput: boolean
  setShowNewCollectionInput: (v: boolean) => void
  newCollectionName: string
  setNewCollectionName: (v: string) => void
  onCreateCollection: () => void
  isCreatingCollection: boolean
}

function CollectionsSidebar({
  collections,
  isLoadingCollections,
  selectedCollectionId,
  onSelectCollection,
  onDeleteCollection,
  showNewCollectionInput,
  setShowNewCollectionInput,
  newCollectionName,
  setNewCollectionName,
  onCreateCollection,
  isCreatingCollection,
}: CollectionsSidebarProps) {
  return (
    <div className="flex flex-col flex-shrink-0">
      <div className="p-3 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-700 font-semibold text-sm">
            <Folders className="w-4 h-4" />
            Colecciones
          </div>
          <button
            type="button"
            title="Nueva coleccion"
            onClick={() => setShowNewCollectionInput(true)}
            className="p-1 rounded hover:bg-slate-200 text-slate-500 hover:text-emerald-600 transition-colors"
          >
            <FolderPlus className="w-4 h-4" />
          </button>
        </div>
      </div>

      <nav className="overflow-y-auto p-2 space-y-0.5 max-h-48">
        <button
          type="button"
          onClick={() => onSelectCollection(null)}
          className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm text-left transition-colors ${
            !selectedCollectionId
              ? 'bg-emerald-50 text-emerald-700 font-medium'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
        >
          <ImageIcon className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1 truncate">Todas</span>
          {!selectedCollectionId && <ChevronRight className="w-3 h-3 flex-shrink-0" />}
        </button>

        {isLoadingCollections ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner size="sm" />
          </div>
        ) : (
          collections.map((col) => (
            <div
              key={col.id}
              className={`group flex items-center gap-2 px-2.5 py-2 rounded-lg text-sm cursor-pointer transition-colors ${
                selectedCollectionId === col.id
                  ? 'bg-emerald-50 text-emerald-700 font-medium'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
              onClick={() => onSelectCollection(selectedCollectionId === col.id ? null : col.id)}
            >
              <Folders className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 truncate">{col.name}</span>
              <button
                type="button"
                title="Eliminar coleccion"
                onClick={(e) => {
                  e.stopPropagation()
                  onDeleteCollection(col.id)
                }}
                className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:text-red-600 transition-all"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))
        )}

        {showNewCollectionInput && (
          <div className="mt-2 px-1">
            <input
              autoFocus
              type="text"
              className="input w-full text-sm mb-1"
              placeholder="Nombre"
              value={newCollectionName}
              onChange={(e) => setNewCollectionName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') onCreateCollection()
                if (e.key === 'Escape') {
                  setShowNewCollectionInput(false)
                  setNewCollectionName('')
                }
              }}
            />
            <div className="flex gap-1">
              <button
                type="button"
                disabled={!newCollectionName.trim() || isCreatingCollection}
                onClick={onCreateCollection}
                className="btn btn-primary text-xs px-2 py-1 flex-1"
              >
                {isCreatingCollection ? <LoadingSpinner size="sm" /> : 'Crear'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNewCollectionInput(false)
                  setNewCollectionName('')
                }}
                className="btn btn-secondary text-xs px-2 py-1"
              >
                X
              </button>
            </div>
          </div>
        )}
      </nav>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export function VisualPage() {
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [selectedCollectionId, setSelectedCollectionId] = useState<string | null>(null)
  const [newCollectionName, setNewCollectionName] = useState('')
  const [showNewCollectionInput, setShowNewCollectionInput] = useState(false)
  const [currentAspectRatio, setCurrentAspectRatio] = useState('1:1')
  const [latestGeneratedImageId, setLatestGeneratedImageId] = useState<string | null>(null)
  const [mobileTab, setMobileTab] = useState<MobileTab>('crear')

  const filters = {
    ...(statusFilter && { status: statusFilter }),
  }

  const {
    tasks,
    isLoading,
    isError,
    error,
    refetch,
    isRefetching,
    cancelTask,
    retryTask,
    createTask,
    isCreating,
    isRetrying,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useVisualTasks(filters)

  const {
    collections,
    isLoading: isLoadingCollections,
    createCollection,
    deleteCollection,
    isCreating: isCreatingCollection,
  } = useVisualCollections()

  const { data: collectionDetail } = useVisualCollectionDetail(selectedCollectionId)
  const { data: taskDetails, isLoading: isLoadingDetails } = useVisualTask(selectedTaskId)
  const { data: taskImages } = useTaskImages(selectedTaskId)

  const collectionTaskIds = selectedCollectionId
    ? new Set(collectionDetail?.items.map((i) => i.visual_task_id) ?? [])
    : null

  const filteredTasks = collectionTaskIds
    ? (tasks?.filter((t) => collectionTaskIds.has(t.id)) ?? [])
    : (tasks ?? [])

  const taskIds = tasks?.map((t) => t.id) ?? []
  const thumbnails = useTaskThumbnails(taskIds)

  const handleCancel = async () => {
    if (selectedTaskId) {
      if (window.confirm('¿Estas seguro de que quieres cancelar esta tarea?')) {
        await cancelTask(selectedTaskId)
        setSelectedTaskId(null)
      }
    }
  }

  const handleRetry = (taskId: string) => retryTask(taskId)

  const handleCreateCollection = async () => {
    const trimmed = newCollectionName.trim()
    if (!trimmed) return
    await createCollection({ name: trimmed })
    setNewCollectionName('')
    setShowNewCollectionInput(false)
  }

  const handleDeleteCollection = async (id: string) => {
    if (!window.confirm('¿Eliminar esta coleccion? Las tareas no se eliminaran.')) return
    if (selectedCollectionId === id) setSelectedCollectionId(null)
    await deleteCollection(id)
  }

  const handleCreateTask = async (data: Parameters<typeof createTask>[0]) => {
    await createTask(data)
    // Switch gallery tab on mobile after creating
    setMobileTab('galeria')
  }

  const handleTaskClick = (task: VisualTask) => {
    setSelectedTaskId(task.id)
    setMobileTab('sesion')
  }

  // Shared sidebar props
  const sidebarProps: CollectionsSidebarProps = {
    collections,
    isLoadingCollections,
    selectedCollectionId,
    onSelectCollection: setSelectedCollectionId,
    onDeleteCollection: handleDeleteCollection,
    showNewCollectionInput,
    setShowNewCollectionInput,
    newCollectionName,
    setNewCollectionName,
    onCreateCollection: handleCreateCollection,
    isCreatingCollection,
  }

  // ─── Task Grid (shared) ──────────────────────────────────────────────────────
  const taskGridContent = (
    <>
      {/* Filters */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Filter className="w-4 h-4 text-slate-500 flex-shrink-0" />
        <select
          className="input w-44 text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">Todos los estados</option>
          <option value="pending">Pendiente</option>
          <option value="running">En proceso</option>
          <option value="completed">Completado</option>
          <option value="failed">Fallido</option>
          <option value="cancelled">Cancelado</option>
        </select>
        {statusFilter && (
          <button
            onClick={() => setStatusFilter('')}
            className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
          >
            Limpiar
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : isError && error ? (
        <QueryErrorState
          title="Error al cargar las tareas visuales"
          error={error}
          onRetry={() => refetch()}
          isRefetching={isRefetching}
        />
      ) : filteredTasks.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filteredTasks.map((task) => (
              <VisualTaskCard
                key={task.id}
                task={task}
                images={(() => {
                  const img = thumbnails[task.id]
                  return img ? [img] : []
                })()}
                onClick={() => handleTaskClick(task)}
              />
            ))}
          </div>
          {!selectedCollectionId && hasNextPage && (
            <div className="flex justify-center mt-6">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="btn btn-secondary"
              >
                {isFetchingNextPage ? 'Cargando...' : 'Cargar mas'}
              </button>
            </div>
          )}
        </>
      ) : (
        <EmptyState
          icon={<ImageIcon className="w-16 h-16" />}
          title={
            selectedCollectionId
              ? 'Esta coleccion esta vacia'
              : statusFilter
              ? 'No se encontraron tareas'
              : 'No hay tareas visuales'
          }
          description={
            selectedCollectionId
              ? 'Agrega tareas desde la galeria usando el icono de carpeta'
              : statusFilter
              ? 'Intenta cambiar los filtros'
              : 'Crea una tarea en la columna de la izquierda'
          }
        />
      )}
    </>
  )

  // ─── Task Detail section (shared) ───────────────────────────────────────────
  const sessionContent = (
    <div className="h-full overflow-y-auto">
      {selectedTaskId ? (
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800 text-sm">Sesion activa</h3>
            <button
              type="button"
              onClick={() => { setSelectedTaskId(null); setLatestGeneratedImageId(null) }}
              className="text-xs text-slate-400 hover:text-slate-600"
            >
              Cerrar
            </button>
          </div>
          {isLoadingDetails ? (
            <div className="flex justify-center py-8">
              <LoadingSpinner />
            </div>
          ) : (
            <p className="text-sm text-slate-500">
              Tarea #{selectedTaskId.slice(0, 8)}…
              {taskImages && taskImages.length > 0 && (
                <span className="ml-2 text-emerald-600 font-medium">
                  {taskImages.length} imagen(es)
                </span>
              )}
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-full text-center p-6 text-slate-400">
          <LayoutGrid className="w-10 h-10 mb-3 opacity-50" />
          <p className="text-sm">Selecciona una tarea para ver los detalles de la sesion aqui</p>
        </div>
      )}
    </div>
  )

  // ─── Mobile Layout (< 768px) ─────────────────────────────────────────────────
  const mobileLayout = (
    <div className="flex flex-col h-full md:hidden">
      {/* Tab Bar */}
      <div className="flex border-b border-slate-200 bg-white sticky top-0 z-10">
        {(
          [
            { id: 'crear' as MobileTab, label: 'Crear', icon: Plus },
            { id: 'galeria' as MobileTab, label: 'Galeria', icon: ImageIcon },
            { id: 'sesion' as MobileTab, label: 'Sesion', icon: Pencil },
          ] as const
        ).map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setMobileTab(id)}
            className={`flex-1 flex flex-col items-center gap-0.5 py-2.5 text-xs font-medium transition-colors border-b-2 ${
              mobileTab === id
                ? 'border-emerald-500 text-emerald-700'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto">
        {mobileTab === 'crear' && (
          <div className="p-4 space-y-4">
            <CanvasWorkspace
              aspectRatio={currentAspectRatio}
              imageId={latestGeneratedImageId}
            />
            {!selectedCollectionId && (
              <div className="card">
                <VisualTaskForm
                  onSubmit={handleCreateTask}
                  isLoading={isCreating}
                  compact
                  onAspectRatioChange={setCurrentAspectRatio}
                />
              </div>
            )}
          </div>
        )}
        {mobileTab === 'galeria' && (
          <div className="p-4">
            <CollectionsSidebar {...sidebarProps} />
            <div className="mt-4">{taskGridContent}</div>
          </div>
        )}
        {mobileTab === 'sesion' && (
          <div className="p-4">{sessionContent}</div>
        )}
      </div>
    </div>
  )

  // ─── Desktop Three-Column Layout (>= 768px) ──────────────────────────────────
  const desktopLayout = (
    <div className="hidden md:flex h-full overflow-hidden">
      <PanelGroup orientation="horizontal" className="h-full">
        {/* Left Panel: Collections + Form */}
        <Panel defaultSize="22%" minSize="15%" maxSize="35%">
          <div className="h-full flex flex-col border-r border-slate-200 bg-slate-50 overflow-hidden">
            <CollectionsSidebar {...sidebarProps} />

            {!selectedCollectionId && (
              <>
                <div className="h-px bg-slate-200" />
                <div className="flex-1 overflow-y-auto p-3">
                  <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                    Nueva tarea
                  </h2>
                  <VisualTaskForm
                    onSubmit={handleCreateTask}
                    isLoading={isCreating}
                    compact
                    onAspectRatioChange={setCurrentAspectRatio}
                  />
                </div>
              </>
            )}
          </div>
        </Panel>

        <ResizeHandle />

        {/* Center Panel: Canvas + Task Grid */}
        <Panel defaultSize="50%" minSize="30%">
          <div className="h-full flex flex-col overflow-hidden">
            {/* Canvas preview area — capped height so the task grid gets space */}
            <div className="p-4 border-b border-slate-200 bg-white flex-shrink-0 max-h-72 overflow-hidden pointer-events-none select-none">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-slate-700">
                  {selectedCollectionId && collectionDetail
                    ? collectionDetail.name
                    : 'Workspace visual'}
                </h2>
                <span className="text-xs text-slate-400">
                  {selectedCollectionId
                    ? `${collectionDetail?.items.length ?? 0} tarea(s)`
                    : 'Generacion Visual'}
                </span>
              </div>
              <CanvasWorkspace
                aspectRatio={currentAspectRatio}
                imageId={latestGeneratedImageId}
                label={`Aspect ratio: ${currentAspectRatio}`}
                className="max-w-[240px] mx-auto"
              />
            </div>

            {/* Task grid */}
            <div className="flex-1 overflow-y-auto p-4">
              {taskGridContent}
            </div>
          </div>
        </Panel>

        <ResizeHandle />

        {/* Right Panel: Session / Detail */}
        <Panel defaultSize="28%" minSize="18%" maxSize="40%">
          <div className="h-full flex flex-col border-l border-slate-200 overflow-hidden">
            <div className="p-3 border-b border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2 text-slate-700 font-semibold text-sm">
                <Pencil className="w-4 h-4" />
                Sesion activa
              </div>
            </div>
            {sessionContent}
          </div>
        </Panel>
      </PanelGroup>
    </div>
  )

  return (
    <div className="h-full flex flex-col">
      {mobileLayout}
      {desktopLayout}

      {/* Detail Modal — shared across layouts */}
      <VisualTaskDetail
        isOpen={!!selectedTaskId}
        onClose={() => setSelectedTaskId(null)}
        task={taskDetails?.tarea || null}
        images={taskImages || null}
        isLoading={isLoadingDetails}
        onCancel={handleCancel}
        onRetry={handleRetry}
        isRetrying={isRetrying}
      />
    </div>
  )
}
